import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  Legend,
} from "recharts";
import TeamMember from "@/components/TeamMember";
import { Badge } from "@/components/ui/badge";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import {
  AlertTriangle,
  ArrowUpRight,
  Lightbulb,
  TrendingUp,
  TrendingDown,
  Users,
  CheckCircle,
  ArrowRight,
} from "lucide-react";
import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { TeamInviteButton } from "@/components/team/TeamInviteButton";
import { useUser } from "@/contexts/UserContext";
import TeamAISuggestionsWidget from "@/components/dashboard/TeamAISuggestionsWidget";
import TeamWellnessHub from "@/components/team/TeamWellnessHub";
import TeamCommunicationHub from "@/components/team/TeamCommunicationHub";

import { useSubscription } from "@/hooks/useSubscription";
import { useEffect, useState } from "react";
import {
  fetchTeamEnergyStats,
  fetchTeamMembersPulse,
  fetchTeamPulseData,
} from "@/components/team/services/teamService";

interface PulseDataPoint {
  name: string;
  team: number;
  personal: number | null;
}

interface GroupedData {
  team: number[];
  personal: number[];
}

interface CheckIn {
  user_id: string;
  date: string;
  energy: number;
}

interface TeamEnergyStats {
  currentAverage: number;
  previousAverage: number;
  percentageChange: number;
  message: string;
}

const teamPulseData = [
  { name: "Mon", team: 75, personal: 80 },
  { name: "Tue", team: 70, personal: 75 },
  { name: "Wed", team: 80, personal: 65 },
  { name: "Thu", team: 65, personal: 70 },
  { name: "Fri", team: 60, personal: 55 },
];

const demoTeamData = [
  {
    name: "Jordan Lee",
    role: "Product Designer",
    status: "great",
    checkInStreak: 7,
    lastCheckIn: "Today",
  },
  {
    name: "Taylor Morgan",
    role: "Developer",
    status: "good",
    checkInStreak: 4,
    lastCheckIn: "Today",
  },
  {
    name: "Riley Johnson",
    role: "Marketing",
    status: "okay",
    checkInStreak: 5,
    lastCheckIn: "Today",
  },
  {
    name: "Casey Williams",
    role: "Developer",
    status: "low",
    checkInStreak: 2,
    lastCheckIn: "Today",
  },
  {
    name: "Avery Smith",
    role: "Project Manager",
    status: "good",
    checkInStreak: 8,
    lastCheckIn: "Today",
  },
  {
    name: "Jamie Garcia",
    role: "Customer Success",
    status: "struggling",
    checkInStreak: 0,
    lastCheckIn: "3 days ago",
  },
];

const TeamPulseChart = () => {
  const { user } = useUser();
  const [pulseData, setPulseData] = useState<PulseDataPoint[]>([]);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    const loadPulseData = async () => {
      try {
        if (!user?.teamId || !user?.id) return;
        const data = await fetchTeamPulseData(user.teamId, user.id);
        setPulseData(data);
      } catch (error) {
        console.error("Error loading pulse data:", error);
      } finally {
        setIsLoading(false);
      }
    };

    loadPulseData();
    
    // Track that user has visited the team page for onboarding checklist
    if (user?.id) {
      localStorage.setItem(`hasVisited_team_${user.id}`, 'true');
    }
  }, [user?.teamId, user?.id]);

  if (isLoading) {
    return (
      <div className="h-64 w-full flex items-center justify-center">
        Loading...
      </div>
    );
  }

  return (
    <div className="h-64 w-full">
      <ResponsiveContainer width="100%" height="100%">
        <BarChart
          data={pulseData}
          margin={{ top: 10, right: 30, left: 0, bottom: 0 }}
        >
          <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
          <XAxis dataKey="name" stroke="#94a3b8" />
          <YAxis stroke="#94a3b8" />
          <Tooltip
            contentStyle={{
              backgroundColor: "white",
              borderRadius: "8px",
              border: "1px solid #f0f0f0",
              boxShadow: "0 4px 6px -1px rgba(0, 0, 0, 0.1)",
            }}
            labelStyle={{ fontWeight: "bold", marginBottom: "4px" }}
          />
          <Legend />
          <Bar
            dataKey="team"
            name="Team Average"
            fill="#9b87f5"
            radius={[4, 4, 0, 0]}
          />
          <Bar
            dataKey="personal"
            name="Your Score"
            fill="#FF8A65"
            radius={[4, 4, 0, 0]}
          />
        </BarChart>
      </ResponsiveContainer>
    </div>
  );
};

const Team = () => {
  const { user } = useUser();
  const navigate = useNavigate();
  const { hasTeamFeatures, hasOrganizationFeatures, planName } = useSubscription();
  const [teamData, setTeamData] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [energyStats, setEnergyStats] = useState<TeamEnergyStats>({
    currentAverage: 0,
    previousAverage: 0,
    percentageChange: 0,
    message: "",
  });
  
  // Check if user is a solo user or doesn't have a team - EARLY CHECK
  // Check personas first, then check if they're in the default organization
  const DEFAULT_ORG_ID = "00000000-0000-0000-0000-000000000001";
  
  // FORCE CHECK: If no teamId, they're a solo user regardless of persona
  const hasNoRealTeam = !user?.teamId || user?.teamId === "";
  const isInDefaultOrg = user?.organizationId === DEFAULT_ORG_ID;
  
  const isSoloUser = 
    user?.persona === "independentSolo" ||
    user?.persona === "soloUser" || // Legacy solo users
    user?.isSoloUser === true || // Direct flag check
    user?.isIndependentSolo === true || // Alternative flag check
    // Check if individual user without team, even if in default org
    (user?.persona === "individual" && hasNoRealTeam && 
     (!user?.organizationId || isInDefaultOrg)) ||
    // FORCE: Any user without a team should see transition page
    (hasNoRealTeam && isInDefaultOrg);
  
  // Organization solo users should see organization interface
  const isOrgSoloUser = user?.persona === "organizationalSolo";
  
  // New team members should see team interface even if teamId is not set yet
  const isNewTeamMember = user?.persona === "newTeamMember" || user?.persona === "newJoiner";
  
  const isTeamAdmin = user?.persona === "manager" || user?.persona === "organizationAdmin" || user?.role === "admin";

  // Determine if we should show transition page (for solo users)
  const shouldShowTransitionPage = (isSoloUser || isOrgSoloUser) && !isNewTeamMember;

  // Load team data on mount and when dependencies change
  // IMPORTANT: This hook must be called unconditionally before any early returns
  useEffect(() => {
    // Skip loading for solo users who will see transition page
    if (shouldShowTransitionPage || !user) {
      setIsLoading(false);
      return;
    }

    const loadTeamData = async () => {
      try {
        if (user?.teamId) {
          const [data, stats] = await Promise.all([
            fetchTeamMembersPulse(user.teamId, isTeamAdmin),
            fetchTeamEnergyStats(user.teamId),
          ]);
          // Filter out the current user from the team data
          const filteredData = data.filter((member) => member.id !== user.id);
          setTeamData(filteredData);
          setEnergyStats(stats);
        } else if (isOrgSoloUser) {
          // For organization solo users, load organization members
          console.log("🏢 Loading organization members for solo user");
          try {
            if (user?.organizationId) {
              // Get organization members via API
              const { organizationsService } = await import('@/services/organizationsService');
              const response = await organizationsService.getMembers(user.organizationId);
              
              if (response && response.data && response.data.length > 0) {
                // Transform to team member format
                const members = response.data.map((member: any) => ({
                  id: member.userId || member.id,
                  name: member.name || member.displayName || 'Unknown',
                  avatarUrl: member.avatarUrl || member.avatar_url,
                  lastCheckIn: "Unknown",
                  status: "active",
                  role: member.role || "member"
                }));

                setTeamData(members.filter(member => member.id !== user.id));
                setEnergyStats({
                  currentAverage: 0,
                  previousAverage: 0,
                  percentageChange: 0,
                  message: "Organization network available.",
                });
              } else {
                setTeamData([]);
                setEnergyStats({
                  currentAverage: 0,
                  previousAverage: 0,
                  percentageChange: 0,
                  message: "No organization members found.",
                });
              }
            } else {
              setTeamData([]);
              setEnergyStats({
                currentAverage: 0,
                previousAverage: 0,
                percentageChange: 0,
                message: "No organization assigned.",
              });
            }
          } catch (error) {
            console.error("Error loading organization members:", error);
            setTeamData([]);
            setEnergyStats({
              currentAverage: 0,
              previousAverage: 0,
              percentageChange: 0,
              message: "Error loading organization data.",
            });
          }
        } else if (isNewTeamMember) {
          // For new team members without teamId, try to find their team
          console.log("🔍 New team member without teamId, attempting to find team data");
          try {
            // Try to find team membership via API
            const { teamsService } = await import('@/services/teamsService');
            const teamsResponse = await teamsService.getUserTeams();
            
            if (teamsResponse && teamsResponse.data && teamsResponse.data.length > 0) {
              const firstTeam = teamsResponse.data[0];
              const teamId = firstTeam.id;
              console.log("✅ Found team for new member:", teamId);
              // Update user context with found team
              // For now, fetch team data with the found team ID
              const [data, stats] = await Promise.all([
                fetchTeamMembersPulse(teamId, isTeamAdmin),
                fetchTeamEnergyStats(teamId),
              ]);
              const filteredData = data.filter((member) => member.id !== user.id);
              setTeamData(filteredData);
              setEnergyStats(stats);
            } else {
              throw new Error("No team found");
            }
          } catch (error) {
            console.warn("❌ Could not find team for new member:", error);
            // Set empty data so they don't see the invitation prompt
            setTeamData([]);
            setEnergyStats({
              currentAverage: 0,
              previousAverage: 0,
              percentageChange: 0,
              message: "Setting up your team data...",
            });
          }
        } else {
          // No team, set empty data with encouraging message
          setTeamData([]);
          setEnergyStats({
            currentAverage: 0,
            previousAverage: 0,
            percentageChange: 0,
            message: "You're in personal mode. Join a team to unlock collaboration features.",
          });
        }
      } catch (error) {
        console.error("Error fetching team data:", error);
      } finally {
        setIsLoading(false);
      }
    };

    loadTeamData();
  }, [user?.teamId, user?.id, user?.organizationId, isTeamAdmin, isOrgSoloUser, isNewTeamMember, shouldShowTransitionPage, user]);
  
  // Always log for debugging
  console.log("=== TEAM PAGE DEBUG (EARLY) ===");
  console.log("User:", user);
  console.log("User persona:", user?.persona);
  console.log("User teamId:", user?.teamId);
  console.log("User teamName:", user?.teamName);
  console.log("User organizationId:", user?.organizationId);
  console.log("User organizationName:", user?.organizationName);
  console.log("User isSoloUser flag:", user?.isSoloUser);
  console.log("User isIndependentSolo flag:", user?.isIndependentSolo);
  console.log("User role:", user?.role);
  console.log("User isNewUser:", user?.isNewUser);
  console.log("Has no real team?", hasNoRealTeam);
  console.log("Is in default org?", isInDefaultOrg);
  console.log("Is solo user?", isSoloUser);
  console.log("Is organization solo user?", isOrgSoloUser);
  console.log("Is new team member?", isNewTeamMember);
  console.log("Should show transition page?", shouldShowTransitionPage);
  console.log("Team data loaded?", teamData.length > 0);
  console.log("Is loading?", isLoading);
  console.log("Is team admin?", isTeamAdmin);

  // If user hasn't loaded yet, show loading
  if (!user) {
    console.log("=== USER NOT LOADED YET ===");
    return (
      <div className="flex items-center justify-center h-full">
        <p>Loading user data...</p>
      </div>
    );
  }
  
  // EARLY RETURN for solo users
  if (shouldShowTransitionPage) {
    console.log("=== SHOWING TRANSITION PAGE FOR SOLO USER ===");
    return (
      <div className="max-w-4xl mx-auto py-8 space-y-8">
          {/* Header */}
          <div className="bg-gradient-to-r from-purple-50 to-blue-50 rounded-lg p-8 border border-purple-200">
            <div className="text-center">
              <div className="mx-auto w-20 h-20 bg-gradient-to-br from-purple-100 to-blue-100 rounded-full flex items-center justify-center mb-6">
                <Users className="w-10 h-10 text-purple-600" />
              </div>
              <h1 className="text-3xl font-bold tracking-tight text-purple-900 mb-4">
                Ready to Collaborate?
              </h1>
              <p className="text-lg text-muted-foreground mb-8 max-w-2xl mx-auto">
                You've been working solo with Step. Transition to team collaboration while keeping all your individual progress and insights.
              </p>
              
              <Button 
                size="lg"
                className="bg-gradient-to-r from-purple-600 to-blue-600 hover:from-purple-700 hover:to-blue-700 text-white shadow-lg"
                onClick={() => navigate("/team-transition")}
              >
                <ArrowRight className="h-5 w-5 mr-2" />
                Start Team Transition
              </Button>
              <p className="text-sm text-muted-foreground mt-3">
                All your personal data and insights will be preserved
              </p>
            </div>
          </div>
          
          {/* Features Grid */}
          <div className="grid md:grid-cols-2 gap-8">
            <Card>
              <CardHeader>
                <CardTitle className="text-xl text-purple-900">Team Features You'll Unlock</CardTitle>
                <CardDescription>Collaborate more effectively with your team</CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <ul className="space-y-4">
                  <li className="flex items-start gap-3">
                    <div className="w-8 h-8 bg-purple-100 rounded-full flex items-center justify-center flex-shrink-0">
                      <span className="text-purple-600 text-sm font-bold">✓</span>
                    </div>
                    <div>
                      <h4 className="font-medium">Share Insights</h4>
                      <p className="text-sm text-muted-foreground">Collaborate with teammates and share progress updates</p>
                    </div>
                  </li>
                  <li className="flex items-start gap-3">
                    <div className="w-8 h-8 bg-purple-100 rounded-full flex items-center justify-center flex-shrink-0">
                      <span className="text-purple-600 text-sm font-bold">✓</span>
                    </div>
                    <div>
                      <h4 className="font-medium">AI-Powered Suggestions</h4>
                      <p className="text-sm text-muted-foreground">Get intelligent team collaboration recommendations</p>
                    </div>
                  </li>
                  <li className="flex items-start gap-3">
                    <div className="w-8 h-8 bg-purple-100 rounded-full flex items-center justify-center flex-shrink-0">
                      <span className="text-purple-600 text-sm font-bold">✓</span>
                    </div>
                    <div>
                      <h4 className="font-medium">
                        {user?.persona === "organizationalSolo" ? "Organization Pulse" : "Team Pulse"}
                      </h4>
                      <p className="text-sm text-muted-foreground">
                        {user?.persona === "organizationalSolo" 
                          ? "Monitor organization morale and energy levels"
                          : "Monitor team morale and energy levels"}
                      </p>
                    </div>
                  </li>
                  <li className="flex items-start gap-3">
                    <div className="w-8 h-8 bg-purple-100 rounded-full flex items-center justify-center flex-shrink-0">
                      <span className="text-purple-600 text-sm font-bold">✓</span>
                    </div>
                    <div>
                      <h4 className="font-medium">Team Building</h4>
                      <p className="text-sm text-muted-foreground">Invite members and build your workspace</p>
                    </div>
                  </li>
                </ul>
              </CardContent>
            </Card>
            
            <Card>
              <CardHeader>
                <CardTitle className="text-xl text-green-900">What Stays the Same</CardTitle>
                <CardDescription>Your individual progress is preserved</CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <ul className="space-y-4">
                  <li className="flex items-start gap-3">
                    <div className="w-8 h-8 bg-green-100 rounded-full flex items-center justify-center flex-shrink-0">
                      <span className="text-green-600 text-sm font-bold">✓</span>
                    </div>
                    <div>
                      <h4 className="font-medium">Check-in History</h4>
                      <p className="text-sm text-muted-foreground">All your check-ins and progress tracking</p>
                    </div>
                  </li>
                  <li className="flex items-start gap-3">
                    <div className="w-8 h-8 bg-green-100 rounded-full flex items-center justify-center flex-shrink-0">
                      <span className="text-green-600 text-sm font-bold">✓</span>
                    </div>
                    <div>
                      <h4 className="font-medium">Personal Insights</h4>
                      <p className="text-sm text-muted-foreground">Your productivity patterns and energy trends</p>
                    </div>
                  </li>
                  <li className="flex items-start gap-3">
                    <div className="w-8 h-8 bg-green-100 rounded-full flex items-center justify-center flex-shrink-0">
                      <span className="text-green-600 text-sm font-bold">✓</span>
                    </div>
                    <div>
                      <h4 className="font-medium">Profile & Skills</h4>
                      <p className="text-sm text-muted-foreground">All your profile information and skills data</p>
                    </div>
                  </li>
                  <li className="flex items-start gap-3">
                    <div className="w-8 h-8 bg-green-100 rounded-full flex items-center justify-center flex-shrink-0">
                      <span className="text-green-600 text-sm font-bold">✓</span>
                    </div>
                    <div>
                      <h4 className="font-medium">Individual Mode</h4>
                      <p className="text-sm text-muted-foreground">Continue focusing on personal productivity</p>
                    </div>
                  </li>
                </ul>
              </CardContent>
            </Card>
          </div>
        </div>
    );
  }
  
  const calculateParticipationRate = (members) => {
    if (!members || members.length === 0)
      return { rate: 0, activeMembers: 0, totalMembers: 0 };

    const today = new Date();
    today.setHours(0, 0, 0, 0);

    const activeMembers = members.filter((member) => {
      if (member.lastCheckIn === "Today") return true;
      if (member.lastCheckIn === "Yesterday") return true;
      return false;
    }).length;

    return {
      rate: Math.round((activeMembers / members.length) * 100),
      activeMembers,
      totalMembers: members.length,
    };
  };

  const currentTeamId = user?.teamId;
  const currentTeamName = user?.teamName;
  console.log("teamData", teamData);
  
  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-full">
        <p>Loading team data...</p>
      </div>
    );
  }

  return (
      <div className="space-y-6">
        {/* Enhanced Team Header */}
        <div className="bg-gradient-to-r from-blue-50 to-cyan-50 rounded-lg p-6 border border-blue-200">
          <div className="flex justify-between items-start mb-4">
            <div>
              <div className="flex items-center gap-3 mb-2">
                <div className="p-2 bg-blue-100 rounded-lg">
                  <Users className="h-6 w-6 text-blue-600" />
                </div>
                <h1 className="text-3xl font-bold tracking-tight text-blue-900">
                  {user?.persona === "organizationalSolo" ? "Organization Pulse" : "Team Pulse"}
                </h1>
              </div>
              <p className="text-muted-foreground text-lg">
                {user?.persona === "organizationalSolo" 
                  ? "Monitor your organization's wellbeing and engagement"
                  : "Monitor your team's wellbeing and engagement"}
              </p>
            </div>

            <TeamInviteButton
              teamId={currentTeamId}
              teamName={currentTeamName}
              variant="default"
              size="lg"
            />
          </div>

          {/* Team stats overview */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <div className="bg-white/60 p-3 rounded-lg border border-blue-100">
              <div className="flex items-center gap-2 mb-1">
                <Users className="h-4 w-4 text-blue-600" />
                <span className="text-sm font-medium text-blue-700">Members</span>
              </div>
              <div className="text-xl font-bold text-blue-800">{teamData?.length || 0}</div>
              <p className="text-xs text-blue-600">Total team size</p>
            </div>
            
            <div className="bg-white/60 p-3 rounded-lg border border-green-100">
              <div className="flex items-center gap-2 mb-1">
                <CheckCircle className="h-4 w-4 text-green-600" />
                <span className="text-sm font-medium text-green-700">Check-ins</span>
              </div>
              <div className="text-xl font-bold text-green-800">
                {teamData?.filter(m => m.lastCheckIn === "Today" || m.lastCheckIn === "Yesterday").length || 0}/{teamData?.length || 0}
              </div>
              <p className="text-xs text-green-600">Team members active</p>
            </div>
            
            <div className="bg-white/60 p-3 rounded-lg border border-amber-100">
              <div className="flex items-center gap-2 mb-1">
                <TrendingUp className="h-4 w-4 text-amber-600" />
                <span className="text-sm font-medium text-amber-700">Energy</span>
              </div>
              <div className="text-xl font-bold text-amber-800">
                {energyStats?.currentAverage || 0}%
              </div>
              <p className="text-xs text-amber-600">
                {user?.persona === "organizationalSolo" ? "Organization average" : "Team average"}
              </p>
            </div>
            
            <div className="bg-white/60 p-3 rounded-lg border border-purple-100">
              <div className="flex items-center gap-2 mb-1">
                <ArrowUpRight className="h-4 w-4 text-purple-600" />
                <span className="text-sm font-medium text-purple-700">Trend</span>
              </div>
              <div className="text-xl font-bold text-purple-800">
                {(energyStats?.percentageChange || 0) > 0 ? '+' : ''}{energyStats?.percentageChange || 0}%
              </div>
              <p className="text-xs text-purple-600">vs last week</p>
            </div>
          </div>
        </div>

        <Tabs defaultValue="overview" className="w-full">
          <TabsList className={`mb-4 grid w-full ${isTeamAdmin ? 'grid-cols-5' : 'grid-cols-4'}`}>
            <TabsTrigger value="overview">Overview</TabsTrigger>
            <TabsTrigger value="members">Members</TabsTrigger>
            {isTeamAdmin && <TabsTrigger value="insights">Insights</TabsTrigger>}
            <TabsTrigger value="wellness">Wellness</TabsTrigger>
            <TabsTrigger value="communication">Collaborate</TabsTrigger>
          </TabsList>

          <TabsContent value="overview" className="space-y-6">
            <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-3">
              <Card>
                <CardHeader className="pb-2">
                  <CardTitle className="text-lg">
                    {user?.persona === "organizationalSolo" ? "Organization Energy" : "Team Energy"}
                  </CardTitle>
                  <CardDescription>
                    Weekly average: {energyStats.currentAverage}%
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="flex items-center justify-between">
                    <div className="text-3xl font-bold flex items-center gap-1 text-primary">
                      {energyStats.currentAverage}%
                      {energyStats.percentageChange > 0 && (
                        <TrendingUp size={20} className="text-green-500" />
                      )}
                      {energyStats.percentageChange < 0 && (
                        <TrendingDown size={20} className="text-red-500" />
                      )}
                    </div>
                    <Badge
                      className={
                        energyStats.percentageChange > 0
                          ? "bg-green-500"
                          : energyStats.percentageChange < 0
                          ? "bg-red-500"
                          : "bg-gray-500"
                      }
                    >
                      {energyStats.percentageChange > 0 ? "+" : ""}
                      {energyStats.percentageChange}% vs. last week
                    </Badge>
                  </div>
                  <p className="text-sm text-muted-foreground mt-2">
                    {energyStats.message}
                  </p>
                </CardContent>
              </Card>

              <Card>
                <CardHeader className="pb-2">
                  <CardTitle className="text-lg">Check-in Rate</CardTitle>
                  <CardDescription>Team participation</CardDescription>
                </CardHeader>
                <CardContent>
                  {(() => {
                    const { rate, activeMembers, totalMembers } =
                      calculateParticipationRate(teamData);
                    const inactiveMember = teamData.find(
                      (member) =>
                        member.lastCheckIn !== "Today" &&
                        member.lastCheckIn !== "Yesterday"
                    );

                    return (
                      <>
                        <div className="flex items-center justify-between">
                          <div className="text-3xl font-bold">{rate}%</div>
                          <Badge variant="outline">
                            {activeMembers} of {totalMembers} members
                          </Badge>
                        </div>
                        {inactiveMember && (
                          <p className="text-sm text-muted-foreground mt-2">
                            {inactiveMember.name} hasn't checked in for{" "}
                            {inactiveMember.lastCheckIn}.
                          </p>
                        )}
                      </>
                    );
                  })()}
                </CardContent>
              </Card>

              {isTeamAdmin && <Card>
                <CardHeader className="pb-2">
                  <CardTitle className="text-lg">Attention Needed</CardTitle>
                  <CardDescription>
                    Team members that may need support
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="space-y-3">
                    {(() => {
                      // Find member with oldest check-in
                      const memberWithOldestCheckIn = teamData.reduce(
                        (oldest, current) => {
                          if (!oldest) return current;
                          if (current.lastCheckIn === "Never") return current;
                          if (oldest.lastCheckIn === "Never") return oldest;

                          const currentDays = parseInt(current.lastCheckIn);
                          const oldestDays = parseInt(oldest.lastCheckIn);

                          if (isNaN(currentDays)) return oldest;
                          if (isNaN(oldestDays)) return current;

                          return currentDays > oldestDays ? current : oldest;
                        },
                        null
                      );

                      // Find member with lowest energy
                      const memberWithLowestEnergy = teamData.reduce(
                        (lowest, current) => {
                          if (!lowest) return current;
                          const energyLevels = {
                            great: 5,
                            good: 4,
                            okay: 3,
                            low: 2,
                            struggling: 1,
                          };
                          return energyLevels[current.status] <
                            energyLevels[lowest.status]
                            ? current
                            : lowest;
                        },
                        null
                      );

                      return (
                        <>
                          {memberWithOldestCheckIn &&
                            memberWithOldestCheckIn.lastCheckIn !== "Today" &&
                            memberWithOldestCheckIn.lastCheckIn !==
                              "Yesterday" && (
                              <div className="flex items-center gap-3 p-2 bg-red-50 dark:bg-red-900/20 rounded-lg border border-red-100 dark:border-red-800/50">
                                <div className="bg-red-100 dark:bg-red-900/30 text-red-600 dark:text-red-400 p-2 rounded-full">
                                  <AlertTriangle size={16} />
                                </div>
                                <div>
                                  <p className="text-sm font-medium text-foreground">
                                    {memberWithOldestCheckIn.name}
                                  </p>
                                  <p className="text-xs text-muted-foreground">
                                    {memberWithOldestCheckIn.lastCheckIn ===
                                    "Never"
                                      ? "has never checked in"
                                      : `hasn't checked in since ${memberWithOldestCheckIn.lastCheckIn}`}
                                  </p>
                                </div>
                              </div>
                            )}

                          {memberWithLowestEnergy &&
                            memberWithLowestEnergy.status !== "great" &&
                            memberWithLowestEnergy.status !== "good" && (
                              <div className="flex items-center gap-3 p-2 bg-amber-50 dark:bg-amber-900/20 rounded-lg border border-amber-100 dark:border-amber-800/50">
                                <div className="bg-amber-100 dark:bg-amber-900/30 text-amber-600 dark:text-amber-400 p-2 rounded-full">
                                  <AlertTriangle size={16} />
                                </div>
                                <div>
                                  <p className="text-sm font-medium text-foreground">
                                    {memberWithLowestEnergy.name}
                                  </p>
                                  <p className="text-xs text-muted-foreground">
                                    Currently feeling{" "}
                                    {memberWithLowestEnergy.status}
                                  </p>
                                </div>
                              </div>
                            )}
                        </>
                      );
                    })()}
                  </div>
                </CardContent>
              </Card>}
            </div>

            <Card>
              <CardHeader>
                <CardTitle>
                  {user?.persona === "organizationalSolo" ? "Organization Pulse Trends" : "Team Pulse Trends"}
                </CardTitle>
                <CardDescription>
                  Compare team energy with your own
                </CardDescription>
              </CardHeader>
              <CardContent>
                <TeamPulseChart />
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle>Quick Status</CardTitle>
                <CardDescription>
                  How is everyone feeling today?
                </CardDescription>
              </CardHeader>
              <CardContent>
                <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
                  {teamData.map((member, index) => (
                    <div
                      key={index}
                      className="border rounded-lg p-3 hover:border-primary/50 transition-colors"
                    >
                      <TeamMember
                        name={member.name}
                        role={member.role}
                        status={member.status as any}
                      />
                      <div className="flex justify-between items-center mt-2 pt-2 border-t text-xs text-muted-foreground">
                        <span>Streak: {member.checkInStreak} days</span>
                        <span>Last check-in: {member.lastCheckIn}</span>
                      </div>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          <TabsContent value="members" className="space-y-6">
            <div className="grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
              {teamData.map((member, index) => (
                <Card key={index}>
                  <CardHeader className="pb-2">
                    <div className="flex justify-between items-start">
                      <div className="flex items-center gap-3">
                        <Avatar>
                          <AvatarFallback>
                            {member.name
                              .split(" ")
                              .map((n: string) => n[0])
                              .join("")}
                          </AvatarFallback>
                        </Avatar>
                        <div>
                          <CardTitle className="text-lg">
                            {member.name}
                          </CardTitle>
                          <CardDescription>{member.role}</CardDescription>
                        </div>
                      </div>
                      <Badge
                        variant={
                          member.status === "great"
                            ? "default"
                            : member.status === "good"
                            ? "secondary"
                            : member.status === "okay"
                            ? "outline"
                            : member.status === "low"
                            ? "destructive"
                            : "destructive"
                        }
                      >
                        {member.status}
                      </Badge>
                    </div>
                  </CardHeader>
                  <CardContent>
                    <div className="space-y-3">
                      <div className="grid grid-cols-2 gap-2">
                        <div className="bg-muted/50 p-2 rounded-lg">
                          <p className="text-xs text-muted-foreground">
                            Check-in streak
                          </p>
                          <p className="font-medium">
                            {member.checkInStreak} days
                          </p>
                        </div>
                        <div className="bg-muted/50 p-2 rounded-lg">
                          <p className="text-xs text-muted-foreground">
                            Last check-in
                          </p>
                          <p className="font-medium">{member.lastCheckIn}</p>
                        </div>
                      </div>

                      {isTeamAdmin && (
                        <div className="bg-muted/50 p-3 rounded-lg">
                          <h4 className="text-sm font-medium mb-1">
                            Recent Reflection
                          </h4>
                          <p className="text-sm text-muted-foreground">
                            {member.lastReflection || "No recent reflection"}
                          </p>
                        </div>
                      )}
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          </TabsContent>

          {isTeamAdmin && <TabsContent value="insights" className="space-y-6">
            {/* AI-Powered Team Insights */}
            <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-3">
              <div className="lg:col-span-1">
                <TeamAISuggestionsWidget />
              </div>
            </div>
            
            <div className="grid gap-6 md:grid-cols-2">
              <Card>
                <CardHeader>
                  <CardTitle>Energy Pattern Insights</CardTitle>
                  <CardDescription>
                    Trends and patterns in your team's energy levels
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="space-y-4">
                    {(() => {
                      const insights = [];

                      // Insight 1: Energy trend
                      if (energyStats.percentageChange !== 0) {
                        insights.push({
                          icon:
                            energyStats.percentageChange > 0
                              ? TrendingUp
                              : TrendingDown,
                          title:
                            energyStats.percentageChange > 0
                              ? "Positive Energy Trend"
                              : "Energy Level Concern",
                          description: energyStats.message,
                          color:
                            energyStats.percentageChange > 0
                              ? "green"
                              : "amber",
                        });
                      }

                      // Insight 2: Check-in participation
                      const {
                        rate,
                        activeMembers,
                        totalMembers: activeTotalMembers,
                      } = calculateParticipationRate(teamData);
                      if (rate < 80) {
                        insights.push({
                          icon: AlertTriangle,
                          title: "Check-in Participation",
                          description: `Only ${rate}% of team members (${activeMembers}/${activeTotalMembers}) have checked in recently. Regular check-ins help maintain team awareness and support.`,
                          color: "amber",
                        });
                      }

                      // Insight 3: Team energy distribution
                      const energyDistribution = {
                        great: teamData.filter((m) => m.status === "great")
                          .length,
                        good: teamData.filter((m) => m.status === "good")
                          .length,
                        okay: teamData.filter((m) => m.status === "okay")
                          .length,
                        low: teamData.filter((m) => m.status === "low").length,
                        struggling: teamData.filter(
                          (m) => m.status === "struggling"
                        ).length,
                      };

                      const totalTeamMembers = Object.values(
                        energyDistribution
                      ).reduce((a, b) => a + b, 0);
                      const positiveEnergy =
                        energyDistribution.great + energyDistribution.good;
                      const positiveEnergyPercentage = Math.round(
                        (positiveEnergy / totalTeamMembers) * 100
                      );

                      if (positiveEnergyPercentage < 60) {
                        insights.push({
                          icon: Lightbulb,
                          title: "Team Energy Distribution",
                          description: `Only ${positiveEnergyPercentage}% of the team is feeling positive energy. Consider team activities or discussions to boost morale.`,
                          color: "blue",
                        });
                      }

                      // If no specific insights, show a positive one
                      if (insights.length === 0) {
                        insights.push({
                          icon: TrendingUp,
                          title: "Team Energy is Stable",
                          description:
                            "Your team's energy levels are consistent and positive. Keep maintaining the good practices that are working well.",
                          color: "green",
                        });
                      }

                      return insights.map((insight, index) => {
                        const getColorClasses = (color: string) => {
                          switch (color) {
                            case 'green':
                              return {
                                bg: 'bg-green-50 dark:bg-green-900/20',
                                border: 'border-green-100 dark:border-green-800/50',
                                iconBg: 'bg-green-100 dark:bg-green-900/30',
                                iconText: 'text-green-600 dark:text-green-400'
                              };
                            case 'amber':
                              return {
                                bg: 'bg-amber-50 dark:bg-amber-900/20',
                                border: 'border-amber-100 dark:border-amber-800/50',
                                iconBg: 'bg-amber-100 dark:bg-amber-900/30',
                                iconText: 'text-amber-600 dark:text-amber-400'
                              };
                            case 'blue':
                              return {
                                bg: 'bg-blue-50 dark:bg-blue-900/20',
                                border: 'border-blue-100 dark:border-blue-800/50',
                                iconBg: 'bg-blue-100 dark:bg-blue-900/30',
                                iconText: 'text-blue-600 dark:text-blue-400'
                              };
                            default:
                              return {
                                bg: 'bg-gray-50 dark:bg-gray-900/20',
                                border: 'border-gray-100 dark:border-gray-800/50',
                                iconBg: 'bg-gray-100 dark:bg-gray-900/30',
                                iconText: 'text-gray-600 dark:text-gray-400'
                              };
                          }
                        };

                        const colors = getColorClasses(insight.color);

                        return (
                          <div
                            key={index}
                            className={`flex gap-3 items-start p-3 ${colors.bg} rounded-lg border ${colors.border}`}
                          >
                            <div className={`${colors.iconBg} ${colors.iconText} p-2 rounded-full h-fit`}>
                              <insight.icon size={18} />
                            </div>
                            <div>
                              <h3 className="font-medium text-sm text-foreground">
                                {insight.title}
                              </h3>
                              <p className="text-sm text-muted-foreground">
                                {insight.description}
                              </p>
                            </div>
                          </div>
                        );
                      });
                    })()}
                  </div>
                </CardContent>
              </Card>

              <Card>
                <CardHeader>
                  <CardTitle>
                    {user?.persona === "organizationalSolo" ? "Organization Recommendations" : "Team Recommendations"}
                  </CardTitle>
                  <CardDescription>
                    {user?.persona === "organizationalSolo" 
                      ? "Suggestions based on organization data"
                      : "Suggestions based on team data"}
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="space-y-4">
                    {(() => {
                      const recommendations = [];

                      // Recommendation 1: Check in on inactive members
                      const inactiveMember = teamData.find(
                        (member) =>
                          member.lastCheckIn !== "Today" &&
                          member.lastCheckIn !== "Yesterday"
                      );

                      if (inactiveMember) {
                        recommendations.push({
                          title: `Check in on ${inactiveMember.name}`,
                          description: `${inactiveMember.name} hasn't checked in since ${inactiveMember.lastCheckIn}. A quick message to check in might be helpful.`,
                          priority: "High Priority",
                          date: "Today",
                        });
                      }

                      // Recommendation 2: Energy level trends
                      if (energyStats.percentageChange < 0) {
                        recommendations.push({
                          title: "Energy Level Drop",
                          description: `Team energy has decreased by ${Math.abs(
                            energyStats.percentageChange
                          )}% compared to last week. Consider organizing a team building activity or an open discussion.`,
                          priority: "Medium Priority",
                          date: "Today",
                        });
                      }

                      // Recommendation 3: Low energy members
                      const lowEnergyMembers = teamData.filter(
                        (member) =>
                          member.status === "low" ||
                          member.status === "struggling"
                      );

                      if (lowEnergyMembers.length > 0) {
                        recommendations.push({
                          title: "Support Struggling Members",
                          description: `${lowEnergyMembers.length} team member(s) are feeling down. An open team discussion might help identify and address any issues.`,
                          priority: "High Priority",
                          date: "Today",
                        });
                      }

                      // If no specific recommendations, show a general one
                      if (recommendations.length === 0) {
                        recommendations.push({
                          title: "Keep Up the Good Work",
                          description:
                            "The team is doing well! Continue maintaining open communication and encouraging regular check-ins.",
                          priority: "Ongoing Practice",
                          date: "Today",
                        });
                      }

                      return recommendations.map((rec, index) => (
                        <div key={index} className="border rounded-lg p-4">
                          <h3 className="font-medium text-sm mb-2">
                            {rec.title}
                          </h3>
                          <p className="text-sm text-muted-foreground mb-3">
                            {rec.description}
                          </p>
                          <div className="flex justify-between text-sm">
                            <span
                              className={`font-medium ${
                                rec.priority === "High Priority"
                                  ? "text-primary"
                                  : rec.priority === "Medium Priority"
                                  ? "text-amber-500"
                                  : "text-blue-500"
                              }`}
                            >
                              {rec.priority}
                            </span>
                            <span className="text-muted-foreground">
                              {rec.date}
                            </span>
                          </div>
                        </div>
                      ));
                    })()}
                  </div>
                </CardContent>
              </Card>
            </div>
          </TabsContent>}

          <TabsContent value="wellness" className="space-y-6">
            <TeamWellnessHub teamId={currentTeamId} />
          </TabsContent>

          <TabsContent value="communication" className="space-y-6">
            <TeamCommunicationHub />
          </TabsContent>
        </Tabs>
      </div>
  );
};

export default Team;
